import React, {useContext} from "react";
import { useTranslation } from "react-i18next";
import { CButton } from "@app/components/Common";
import { Text, View } from "@/styles/reactnative";
import { COLORS } from "@app/constants";
import { useTradeModalStore } from "@app/hooks/trade/modalStore";
import TIcon from '../../../../../pages/QBIT/Qp2pPage/assets/TIcon.svg?react'
import styled, { keyframes } from "styled-components";
import {Popover } from 'antd';
import {useIsMobile, useIsTablet} from "@utils/CalVW.tsx";
import { IconDotsVertical } from "@tabler/icons-react";
import {ThemeContext} from "@/theme/ThemeProvider.tsx";
import { formatDecimal4Truncate } from "@/utils/formatDecimal";
import { truncateWithComma } from "@/utils/Truncate";

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
`;

const StyledTableRow = styled(View)`
  width: 100%;
  min-height: 60px;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 24px;
  border-bottom: 1px solid ${({theme}) => theme.COLORS.border || 'rgba(255, 255, 255, 0.05)'};
  transition: all 0.3s ease;
  animation: ${slideInUp} 0.5s ease-out;
  position: relative;
  background: ${({theme}) => theme.itemBg || '#ffffff'};

  &:hover {
    background: linear-gradient(
      135deg,
      ${({theme}) => theme.itemBg || '#ffffff'} 0%,
      ${({theme}) => theme.COLORS.primary || '#007bff'}08 100%
    );
    transform: translateX(4px);
    box-shadow: 
      0 2px 8px rgba(0, 0, 0, 0.1),
      inset 3px 0 0 ${({theme}) => theme.COLORS.primary || '#007bff'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StyledTableCell = styled(Text)`
  font-size: 15px;
  width: 25%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  color: ${({theme}) => theme.COLORS.textw || '#ffffff'};
  transition: all 0.3s ease;
  padding: 12px 8px;

  &:first-child {
    font-weight: 600;
    color: ${({theme}) => theme.COLORS.primary || '#007bff'};
  }
`;

const StyledMobileCard = styled.div`
  width: 100%;
  padding: 20px 15px;
  margin-bottom: 16px;
  background: ${({theme}) => theme.itemBg || '#ffffff'};
  border-radius: 12px;
  border: 1px solid ${({theme}) => theme.COLORS.border || 'rgba(255, 255, 255, 0.1)'};
  box-shadow: 
    0 2px 12px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  animation: ${slideInUp} 0.5s ease-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 4px 20px rgba(0, 0, 0, 0.12),
      0 2px 6px rgba(0, 0, 0, 0.08);
    border-color: ${({theme}) => theme.COLORS.primary || '#007bff'};
  }
`;

const StyledMobileRow = styled(Text)`
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 12px;
  font-weight: 500;
  color: ${({theme}) => theme.COLORS.textw || '#ffffff'};
  padding: 8px 0;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CoinLabel = styled.span`
  font-weight: 600;
  color: ${({theme}) => theme.COLORS.primary || '#007bff'};
  margin-right: 8px;
  min-width: 120px;
  display: inline-block;
`;

const ValueText = styled.span`
  font-weight: 600;
  color: ${({theme}) => theme.COLORS.textw || '#ffffff'};
  font-family: 'Monaco', 'Menlo', monospace;
`;

const IconSVG = styled.svg`
  width: 24px;
  height: 24px;
  margin-right: 8px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const  AssetSpotTableList = ({ data }) => {
  const { t } = useTranslation();
  const { data: modalStore, mutate: muatateModalStore } = useTradeModalStore();
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const { isDarkMode } = useContext(ThemeContext);

  return (
    <View
      style={{
        width: "100%",
        paddingBlock: 10,
      }}
    >
      {isMobile ?
        data?.map((item: any, index: number) => (
          <div style={{width: '100%', paddingInline: '15px'}}>
            <div >
              <Text
                style={{
                  fontSize: 15,
                  justifyContent: "left",
                  marginBottom: 10
                }}
              >
                {t('coin')} :
                <IconSVG as={TIcon} style={{marginRight: isMobile ? 5 : 10}}/>
                {item?.coin}
              </Text>
              <Text
                style={{
                  fontSize: 15,
                  justifyContent: "left",
                  marginBottom: 10
                }}
              >
                {t('balance')} :
                {truncateWithComma(item?.balance, 4)} 
              </Text>
              <Text
                style={{
                  fontSize: 15,
                  justifyContent: "left",
                  marginBottom: 10
                }}
              >
                {t('availableBalance')} :
                {truncateWithComma(item?.availableBalance, 4)}
              </Text>
              <Text
                style={{
                  fontSize: 15,
                  justifyContent: "left",
                  marginBottom: 10
                }}
              >
                {t('inOrderWithdrawals')} :
                {truncateWithComma(item?.inOrderWithdrawals, 4)}
              </Text>
            </div>
          </div>
        ))
        :
        data?.map((item: any, index: number) => (
        <View
          key={index.toString()}
          style={{
            width: "100%",
            height: 50,
            flexDirection: "row",
          }}
        >
          <Text
            style={{
              fontSize: 15,
              width: `25%`,
              justifyContent: "center",
            }}
          >
            <IconSVG as={TIcon} style={{marginRight: isMobile ? 5 : 10}}/>
            {item?.coin}
          </Text>
          <Text
            style={{
              fontSize: 15,
              width: `25%`,
              justifyContent: "center",
            }}
          >
            {truncateWithComma(item?.balance, 4)}
          </Text>
          <Text
            style={{
              fontSize: 15,
              width: `25%`,
              justifyContent: "center",
            }}
          >
            {truncateWithComma(item?.availableBalance, 4)}
          </Text>
          <Text
            style={{
              fontSize: 15,
              width: `25%`,
              justifyContent: "center",
            }}
          >
            {truncateWithComma(item?.inOrderWithdrawals, 4)}
          </Text>
          {/* {isTablet ?
         <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
           <Popover
             placement="bottom"
             overlayInnerStyle={{backgroundColor: isDarkMode ? '#333333' : '#EFD9D0'}}
             arrow={false}
             content={
               <View style={{ width: "100%", flexDirection: "column", gap: 20, backgroundColor: isDarkMode ? '#333333' : '#EFD9D0' }}>
                 <CButton
                   title={t("deposit")}
                   style={{ color: COLORS.secondary }}
                   onClick={() => {
                     muatateModalStore({
                       ...modalStore,
                       depositModal: true,
                     });
                   }}
                 />
                 <CButton
                   title={t("withdrawList")}
                   style={{ color: COLORS.secondary }}
                   onClick={() => {
                     muatateModalStore({
                       ...modalStore,
                       withdrawModal: true,
                     });
                   }}
                 />
                 <CButton
                   title={t("transfer")}
                   style={{ color: COLORS.secondary }}
                   onClick={() => {
                     muatateModalStore({
                       ...modalStore,
                       transferModal: true,
                     });
                   }}
                 />
               </View>
             }
           >
             <IconDotsVertical style={{ color: COLORS.secondary, cursor: "pointer" }} />
           </Popover>
         </div>
          :
            <View style={{ width: "20%", flexDirection: "row",   justifyContent: "left" }}>
              <CButton
                title={t("deposit")}
                style={{ color: COLORS.secondary, paddingLeft: 0 }}
                onClick={() => {
                  muatateModalStore({
                    ...modalStore,
                    depositModal: true,
                  });
                }}
              />
              <CButton
                title={t("withdrawList")}
                style={{ color: COLORS.secondary }}
                onClick={() => {
                  muatateModalStore({
                    ...modalStore,
                    withdrawModal: true,
                  });
                }}
              />
              <CButton
                title={t("transfer")}
                style={{ color: COLORS.secondary }}
                onClick={() => {
                  muatateModalStore({
                    ...modalStore,
                    transferModal: true,
                  });
                }}
              />
            </View>
          } */}

        </View>
      ))}
    </View>
  );
};

export default AssetSpotTableList;

